import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import { uploadFilesAndGetIds, hardDeletePresignedFiles } from "components/Imageshelper";
import ConnectVendorModal from "components/ConnectVendorModal";
import VendorDetailsSlider from "components/VendorDetailsSlider";
import "../../assets/css/SignageOrders.css";
import { getAuthCookie } from "../../utils/authCookies";

const API = process.env.REACT_APP_API_URL;

// ---------- Fallback (shown if fetch/token fails) ----------
const DEFAULT_PROFILE = {
    _id: "fallback",
    vendor_name: "SignMax Pro",
    tag_line: "Premium Real Estate Signage Solutions",
    description:
        "SignMax Pro has been the trusted choice for real estate professionals nationwide, delivering premium signage solutions that help properties sell faster.",
    core_specialties: ["Yard Signs", "Open House Signs", "Banners", "Custom Designs"],
};

const DEFAULT_CATEGORIES = [
    { id: "yard", name: "Yard Signs", product_count: 2 },
    { id: "banners", name: "Banners", product_count: 1 },
] as CategoryRow[];
// -----------------------------------------------------------

export type VendorProfile = {
    _id: string;
    user_id: string;
    vendor_name: string;
    category: string;
    tag_line?: string;
    description?: string;
    about_company?: string;
    logo?: string;
    banner?: string;
    contact_info?: {
        website?: string;
        email?: string;
        phone?: string;
        location?: {
            label?: string;
            addressLine1?: string;
            addressLine2?: string;
            city?: string;
            state?: string;
            country?: string;
            zip?: string;
            cityCode?: string;
            stateCode?: string;
            countryCode?: string;
            latitude?: number;
            longitude?: number;
            phone?: string;
        };
    };
    business_details?: {
        year_of_established?: number;
        number_of_employees?: number;
        response_time?: number;
        completed_projects?: number;
        price_model?: string;
    };
    core_specialties?: string[];
    certifications_compliance?: string[];
    why_choose_us?: string;
    verified?: boolean;
    partner?: boolean;
    status?: string;
    catalog_visibility?: string;
};
type CategoryRow = {
    _id?: string;
    id?: string;
    category_id?: string;
    name?: string;
    label?: string;
    title?: string;
    slug?: string;
    product_count?: number;
    images?: any[];
    image?: any;
    count?: number;
    products?: any[];
};

type BrandRow = {
    _id?: string | null;
    brand_id?: string | null;
    brand_name?: string;
    name?: string;
    logo?: string;
    url?: string;
    // id or http url
    category_count?: number;     // categories
    product_count?: number;      // products
};
type Props = {
    token?: string | null;
    onViewCategory?: (cat: CategoryRow) => void;
    supplierId?: string; // 👈 add this
};

type ApiCategoriesResponse = {
    data: Array<{ category: string; count: number, image: any, category_id: string, _id: string, id: string }>;
};
export default function MarketplaceBrands({ onViewCategory }: Props) {
    const { id, supplierId } = useParams<{ id: string, supplierId: string }>();
    const navigate = useNavigate();

    localStorage.setItem("supplierId", supplierId || "");
    localStorage.setItem("profileId", id || "");
    // const supplierId = localStorage.getItem("supplierId");
    // console.log("id" ,supplierId);
    // read/sanitize token once
    const raw = getAuthCookie("sessionToken") || "";
    const token = raw.replace(/^"|"$/g, ""); // some apps store with quotes
    const userId = getAuthCookie("userId");
    const [profile, setProfile] = useState<VendorProfile | null>(null);
    const [brands, setBrands] = useState<BrandRow[]>([]);
    const [activeProfile, setActiveProfile] = useState<VendorProfile | null>(null);
    const location = useLocation();
    const isTeammember = location.pathname.includes('/teammember');
    const isAgent = location.pathname.includes('/agent');
    const baseRoute = isTeammember ? '/teammember' : isAgent ? '/agent' : '/realtor';

    const [loading, setLoading] = useState(true);
    // Draft typing value; applied value is only updated on Enter
    const [brandSearchInput, setBrandSearchInput] = useState("");
    const [brandSearch, setBrandSearch] = useState("");
    const [brandSort, setBrandSort] = useState<string>("default");
    const [showDetails, setShowDetails] = useState(false);
    const [showConnectModal, setShowConnectModal] = useState(false);

    type UploadedImage = { id: string; name: string; size: number };
    const [uploaded, setUploaded] = useState<UploadedImage[]>([]);
    const [tempUploadIds, setTempUploadIds] = useState<Record<string, string[]>>({});

    // Collect ids we might display
    const allImageIds: string[] = useMemo(() => {
        const ids: string[] = [];

        // category images
        (brands || []).forEach((r) => {
            const id = (r?.logo?.length ? r.logo : r?.logo) as any;
            if (id) ids.push(String(id));
        });

        // ✅ include the vendor logo id
        if (profile?.logo) ids.push(String(profile.logo));

        // newly uploaded in this session
        uploaded.forEach((u) => ids.push(u.id));

        return Array.from(new Set(ids));
    }, [brands, uploaded, profile?.logo]);




    // Payload expected by useVariantImageUrlMap
    const dataForMap = useMemo(() => {
        if (allImageIds.length === 0) return [];
        return [{
            product: { variants: [{ images: allImageIds.map((url) => ({ url })) }] },
        }];
    }, [allImageIds]);

    const imageUrlMap = useVariantImageUrlMap(dataForMap, token);

    const logoUrl = useMemo(() => {
        const id = profile?.logo;
        if (!id) return "";

        // via map first (handles ids presigned by the helper)
        const viaMap = getImageSrc(id, imageUrlMap);
        if (viaMap) return viaMap;

        // if already an http(s) url, use as-is
        if (/^https?:\/\//i.test(String(id))) return String(id);

        // last fallback if backend serves “/files/:id”
        return `${process.env.REACT_APP_BASE_URL}${id}`;
    }, [profile?.logo, imageUrlMap]);


    useEffect(() => {
        let abort = new AbortController();

        async function load() {
            setLoading(true);

            // If no token, show fallback immediately
            if (!token) {
                setProfile(DEFAULT_PROFILE as VendorProfile);
                // setBrands(DEFAULT_CATEGORIES);
                setLoading(false);
                return;
            }

            const commonInit: RequestInit = {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                signal: abort.signal,
            };

            try {
                // 1) Profiles
                let pOk = false;
                try {
                    const resp = await fetch(`${API}/marketplace-profiles/${id}`, {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    });
                    const json = (await resp.json()) as VendorProfile | { error?: string };
                    if (!resp.ok) throw new Error((json as any)?.error || "Failed to load vendor details");
                    setProfile(json as VendorProfile);
                } catch { }
                // if (!pOk) setProfile(DEFAULT_PROFILE);

                // 2) Categories
                let cOk = false;
                try {
                    const bRes = await fetch(`${API}/signage/${supplierId}/marketplace/brands`, commonInit);
                    if (bRes.ok) {
                        const bJson = await bRes.json(); // { data: [...] } like your sample
                        const parsed: BrandRow[] = Array.isArray(bJson?.data)
                            ? bJson.data
                                .filter((b: any) => b?.brand_id) // drop the aggregate row with null brand_id
                                .map((b: any) => ({
                                    _id: b._id,
                                    brand_id: b.brand_id,
                                    brand_name: b.brand_name,
                                    logo: b.logo,
                                    category_count: b.category_count ?? 0,
                                    product_count: b.product_count ?? 0,
                                }))
                            : [];
                        setBrands(parsed);
                    } else {
                        setBrands([]);
                    }
                } catch {
                    setBrands([]);
                }
            } finally {
                setLoading(false);
            }
        }

        load();
        return () => abort.abort();
    }, [token]);

    const title = useMemo(() => {
        const vendor = profile?.vendor_name?.trim() || "Vendor";
        return `${vendor} - Brands & Products`;
    }, [profile]);

    const displayBrands = useMemo(() => {
        let list = [...brands];
        const term = brandSearch.trim().toLowerCase();
        if (term) {
            list = list.filter(
                (b) =>
                    (b.brand_name || b.name || "")
                        .toLowerCase()
                        .includes(term)
            );
        }
        if (brandSort === "name-asc") list.sort((a, b) => (a.brand_name || a.name || "").localeCompare(b.brand_name || b.name || ""));
        if (brandSort === "name-desc") list.sort((a, b) => (b.brand_name || b.name || "").localeCompare(a.brand_name || a.name || ""));
        if (brandSort === "products-high") list.sort((a, b) => (b.product_count ?? 0) - (a.product_count ?? 0));
        if (brandSort === "products-low") list.sort((a, b) => (a.product_count ?? 0) - (b.product_count ?? 0));
        return list;
    }, [brands, brandSearch, brandSort]);

    function categoryName(c: CategoryRow) {
        // also handle raw {category}
        return (c as any).category || c.name || c.label || c.title || "Category";
    }
    function categoryCount(c: CategoryRow) {
        return typeof c.product_count === "number"
            ? c.product_count
            : typeof c.count === "number"
                ? c.count
                : Array.isArray(c.products)
                    ? c.products.length
                    : 0;
    }

    const vendorName = profile?.vendor_name?.trim() || "Vendor";
    const logoColors = ["#FF6B6B", "#4ECDC4", "#FFE66D", "#95E1D3", "#A8EDEA", "#FFB6B9", "#667BC6", "#FF9671", "#A0C4FF"];

    const closeDetails = () => setShowDetails(false);
    const userType = isTeammember ? "agent" : isAgent ? "agent" : "realtor";

    return (
        <div className="overlay-page active" style={{ display: "block" }}>
            {/* <div className="overlay-page-header">
                <div className="overlay-page-header-content">
                    <div className="header-left">
                        <Link to={`${baseRoute}/suppliers`} className="back-btn" style={{ textDecoration: "none" }}>
                            ←
                        </Link>
                        <span className="page-title-text">Brands & Products</span>
                    </div>
                </div>
            </div> */}

            <div className="overlay-page-content">
                <div className="vendor-info-card" id="catalogVendorCard">
                    <div className="vendor-info-card-left">
                        <Link to={`${baseRoute}/suppliers`} className="vendor-info-card-back" style={{ textDecoration: "none" }}>
                            ←
                        </Link>
                        {/* <div
                            className="vendor-info-card-logo"
                            style={{
                                background: logoUrl ? "transparent" : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                color: logoUrl ? "inherit" : "white",
                            }}
                        >
                            {logoUrl ? (
                                <img src={logoUrl} alt={vendorName} style={{ width: 44, height: 44, objectFit: "contain", borderRadius: 10 }} />
                            ) : (
                                vendorName.charAt(0).toUpperCase()
                            )}
                        </div> */}
                        <span className="vendor-info-card-name">{vendorName}</span>
                    </div>
                    <div className="vendor-info-card-right">
                        <button type="button" className="btn-header-details" onClick={() => setShowDetails(true)}>
                            Details
                        </button>
                        <button
                            type="button"
                            className="btn-connect-header"
                            onClick={() => {
                                if (supplierId) localStorage.setItem("supplierId", supplierId);
                                setShowConnectModal(true);
                            }}
                        >
                            ✓ Connect
                        </button>
                    </div>
                </div>

                <div className="breadcrumb" id="catalogBreadcrumb">
                    <span className="breadcrumb-item" onClick={() => navigate(`${baseRoute}/suppliers`)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && navigate(`${baseRoute}/suppliers`)}>
                        {vendorName}
                    </span>
                    <span className="breadcrumb-separator">›</span>
                    <span className="breadcrumb-item active">Brands</span>
                </div>

                <div className="page-header-centered">
                    <h1 className="page-title" id="catalogPageTitle">{title}</h1>
                    <p className="page-subtitle" id="catalogPageSubtitle">Browse brands and explore our complete catalog</p>
                </div>

                <div className="search-filter-card">
                    <div className="search-filter-left">
                        <span className="results-count" id="brandsResultsCount">
                            <strong>{displayBrands.length}</strong> brands
                        </span>
                    </div>
                    <div className="search-filter-right">
                        <div className="filter-search-input">
                            <span className="search-icon">🔍</span>
                            <input
                                type="text"
                                id="catalogSearchInput"
                                placeholder="Search brands..."
                                value={brandSearchInput}
                                onChange={(e) => setBrandSearchInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        setBrandSearch(brandSearchInput);
                                    }
                                }}
                            />
                        </div>
                        <div className="filter-divider" />
                        <span className="filter-label">Sort by:</span>
                        <select
                            className="filter-select"
                            id="brandsSortSelect"
                            value={brandSort}
                            onChange={(e) => setBrandSort(e.target.value)}
                        >
                            <option value="default">Default</option>
                            <option value="name-asc">Name (A-Z)</option>
                            <option value="name-desc">Name (Z-A)</option>
                            <option value="products-high">Most Products</option>
                            <option value="products-low">Fewest Products</option>
                        </select>
                    </div>
                </div>

                {loading ? (
                    <div className="card1-grid">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="card1">
                                <div className="card1-header">
                                    <div className="card1-logo" style={{ background: "#e0e0e0", color: "#999" }}>—</div>
                                </div>
                                <div className="card1-body">
                                    <div className="card1-name">Loading...</div>
                                    <div className="card1-description">—</div>
                                    <div className="card1-stats">
                                        <div className="card1-stat"><span className="card-stat-label">Categories</span><span className="card-stat-value">—</span></div>
                                        <div className="card1-stat"><span className="card-stat-label">Products</span><span className="card-stat-value">—</span></div>
                                    </div>
                                    <div className="card1-footer">
                                        <span className="btn-card-action" style={{ cursor: "default", opacity: 0.7 }}>View</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="card1-grid" id="catalogBrandsGrid">
                        {displayBrands.map((b, idx) => {
                            const name = b.brand_name || b.name || "—";
                            const imgId = b.logo || "";
                            const imgUrl =
                                getImageSrc(imgId, imageUrlMap) ||
                                (/^https?:\/\//i.test(String(imgId)) ? String(imgId) : imgId ? `${process.env.REACT_APP_BASE_URL}${imgId}` : "");

                            return (
                                <div key={b._id || b.brand_id || b.brand_name || idx} className="card1">
                                    <div className="card1-header">
                                        <div
                                            className="card1-logo"
                                            style={{
                                                background: imgUrl ? "transparent" : `linear-gradient(135deg, ${logoColors[idx % logoColors.length]}, ${logoColors[(idx + 1) % logoColors.length]})`,
                                                color: "white",
                                            }}
                                        >
                                            {imgUrl ? (
                                                <img src={imgUrl} alt={name} style={{ width: 270, height: 160 }} />
                                            ) : (
                                                name.substring(0, 2).toUpperCase()
                                            )}
                                        </div>
                                    </div>
                                    <div className="card1-body" style={{ textAlign: "center" }}>
                                        <div className="card1-name">{name}</div>
                                        <div className="card1-description">
                                            {b.product_count ?? 0} product{(b.product_count ?? 0) !== 1 ? "s" : ""} available
                                        </div>
                                        <div className="card1-stats">
                                            <div className="card1-stat">
                                                <span className="card1-stat-label">Categories</span>
                                                <span className="card1-stat-value">{b.category_count ?? 0}</span>
                                            </div>
                                            <div className="card1-stat">
                                                <span className="card1-stat-label">Products</span>
                                                <span className="card1-stat-value">{b.product_count ?? 0}</span>
                                            </div>
                                        </div>
                                        <div className="card-footer">
                                            <Link
                                                to={`${baseRoute}/suppliers/${supplierId}/category/${b.brand_id || b._id}`}
                                                state={{ brand: b }}
                                                className="btn-card-action"
                                                style={{ display: "block", textAlign: "center", textDecoration: "none", color: "white" }}
                                            >
                                                View Categories →
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {!loading && displayBrands.length === 0 && (
                    <div className="card1-grid">
                        <div className="card1" style={{ gridColumn: "1 / -1", textAlign: "center", padding: "2rem" }}>
                            No brands available.
                        </div>
                    </div>
                )}
            </div>

            <VendorDetailsSlider
                show={showDetails}
                onClose={closeDetails}
                profile={profile}
                logoUrlMap={imageUrlMap}
                onConnect={() => { if (supplierId) localStorage.setItem("supplierId", supplierId); setShowConnectModal(true); }}
            />

            <ConnectVendorModal
                show={showConnectModal}
                onClose={() => setShowConnectModal(false)}
                vendor={profile ? {
                    id: supplierId || profile._id || "",
                    name: vendorName,
                    logo: logoUrl || undefined,
                } : null}
                userType={userType}
            />
        </div>
    );
}
